import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiPlus, FiTrash2 } from "react-icons/fi";

import { useAppState } from "../../../context/StateContext";
import { GeneralComments } from "../../../models/general.comments";
import ConfirmationModal from "../../Dialogs/ConfirmationModal";
import { Color } from "../../Form/Button";
import SkeletonComponent from "../Skeleton";
import AddCommentModal from "./AddCommentModal";
import { Comment } from "./Comment";
import {
  ButtonLabel,
  CommentsTitle,
  CommentsTitleContainer,
  CommentsWrapper,
  DeleteText,
  EmptyCommentMessage,
  EmptyCommentViewMessage,
  EmptyCommentsTitle,
  EmptyState,
  EmptyStateWrapper,
  ModalText,
  NewCommentButton,
  useStyles,
} from "./styles";

interface GeneralCommentsProps {
  isViewMode?: boolean;
  enableEmptyState?: boolean;
  comments?: GeneralComments[];
  generalCommentsTitle?: string;
  onAddComment?: (comment: GeneralComments) => void;
  onRemoveComment?: (comments: GeneralComments[], generalCommentRemoved: GeneralComments) => void;
  isLoading?: boolean;
  className?: string;
  isCustomerPortal?: boolean;
  isVideoSurvey?: boolean;
}

const EMPTY_COMMENTS = require("../../../assets/img/empty-comments.svg");

/**
 * @description This component is responsible to show the comment section of Quote Page.
 * In it, we can add a comment, remove a comment and list all comments.
 *
 * @param isViewMode {Boolean} Whether the comment should be shown as view mode
 * @param comments {GeneralComment[]} An array of comments that have a message, a userId and a createdAt date.
 * @param onAddComment {Function} A function that receive a comment: GeneralComment to add into array of comments.
 * @param onRemoveComment {Function} A function that receive an array of comments to update the old array.
 * @param isLoading {Boolean} Flag to identify if loading.
 * @returns {GeneralComment[]} a list of quote comments.
 */
const GeneralCommentsComponent = (props: GeneralCommentsProps) => {
  const {
    isViewMode = false,
    enableEmptyState = true,
    comments,
    onAddComment,
    onRemoveComment,
    generalCommentsTitle,
    className,
    isLoading = false,
    isCustomerPortal = false,
    isVideoSurvey = false,
  } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [openAddComment, setOpenAddComment] = useState<boolean>(false);
  const [openDeleteCommentModal, setOpenDeleteCommentModal] = useState<boolean>(false);
  const [commentToDeleteIndex, setCommentToDeleteIndex] = useState<number>(0);

  const classes = useStyles();

  const handleAddNewComment = (comment: string) => {
    const commentObj = {
      createdAt: new Date(),
      message: comment,
      userId: currentUser?.id,
      user: currentUser,
    } as GeneralComments;

    if (onAddComment) onAddComment(commentObj);

    setOpenAddComment(false);
  };

  const handleRemoveComment = (index: number) => {
    const clonnedComments = [...(comments || [])];
    const commentRemoved = clonnedComments[index];

    if (!clonnedComments.length) return;

    clonnedComments.splice(index, 1);

    if (onRemoveComment) onRemoveComment(clonnedComments, commentRemoved);

    setOpenDeleteCommentModal(false);
  };

  const renderContent = () => {
    return (
      <CommentsWrapper className={className}>
        <ConfirmationModal
          className={classes.modal}
          open={openDeleteCommentModal}
          closeModal={() => setOpenDeleteCommentModal(false)}
          type="error"
          title={translate("deleteComment")}
          leftIconTitle={<FiTrash2 color="#F04438" size={20} />}
          customColorCancel={Color.TEXT_INFO}
          customColorSave={Color.ERROR}
          textSubmit={translate("delete")}
          submit={() => handleRemoveComment(commentToDeleteIndex)}
        >
          <ModalText>{translate("areYouSureYouWantToDeleteThisComment")}</ModalText>
          <DeleteText>{translate("thisActionIsPermanentAndCannotBeUndone")}</DeleteText>
        </ConfirmationModal>

        <AddCommentModal
          open={openAddComment}
          closeModal={() => setOpenAddComment(false)}
          handleAddNewComment={handleAddNewComment}
        />

        <Grid container direction="column">
          <CommentsTitleContainer
            container
            item
            alignItems="center"
            justifyContent="space-between"
            xs={12}
            md={12}
            lg={12}
          >
            <CommentsTitle>{generalCommentsTitle || translate("generalComments")}</CommentsTitle>
            {!isViewMode && comments && !!comments?.length && (
              <NewCommentButton
                startIcon={<FiPlus size={20} />}
                onClick={() => setOpenAddComment(true)}
                disabled={isLoading}
              >
                <ButtonLabel>{translate("newComment")}</ButtonLabel>
              </NewCommentButton>
            )}
          </CommentsTitleContainer>
          {
            // eslint-disable-next-line no-nested-ternary
            !comments || !comments?.length ? (
              enableEmptyState ? (
                <Grid
                  container
                  item
                  xs={12}
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                >
                  <img src={EMPTY_COMMENTS} alt={translate("noData")} />
                  <EmptyState>
                    <EmptyStateWrapper>
                      <EmptyCommentsTitle>
                        {translate("itLooksLikeTheresNoCommentsYet")}
                      </EmptyCommentsTitle>
                      <EmptyCommentMessage>
                        {translate("feelFreeToAddAnyCommentsYouFeelNecessary")}
                      </EmptyCommentMessage>
                    </EmptyStateWrapper>

                    <NewCommentButton
                      startIcon={<FiPlus size={20} />}
                      onClick={() => setOpenAddComment(true)}
                      disabled={isLoading}
                      isEmpty={true}
                    >
                      {translate("newComment")}
                    </NewCommentButton>
                  </EmptyState>
                </Grid>
              ) : (
                <EmptyCommentViewMessage>{translate("noCommentsAdded")}</EmptyCommentViewMessage>
              )
            ) : null
          }
          <Grid container spacing={2}>
            {comments?.map((comment, index) => (
              <Grid
                key={`comment-${index}`}
                item
                xs={12}
                sm={isViewMode || isCustomerPortal ? 12 : 6}
                md={isVideoSurvey ? 12 : 6}
                lg={isVideoSurvey ? 12 : 6}
              >
                <SkeletonComponent classesName={classes.skeleton} isLoading={isLoading}>
                  <Comment
                    comment={comment}
                    isViewMode={isViewMode}
                    handleDeleteComment={() => {
                      setCommentToDeleteIndex(index);
                      setOpenDeleteCommentModal(true);
                    }}
                  />
                </SkeletonComponent>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </CommentsWrapper>
    );
  };

  return <>{renderContent()}</>;
};

export default GeneralCommentsComponent;
